import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import heroVideo from '@assets/Plasmics_Image_Video_1792501034655.mp4';
import posterImg from '@/assets/hero-sample-poster.jpg';

const RM = '(prefers-reduced-motion: reduce)';

export function HeroVideoBackground() {
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && window.matchMedia(RM).matches);
  const [userPaused, setUserPaused] = useState(false);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const [visible, setVisible] = useState(true);
  const [tabShown, setTabShown] = useState(() => typeof document === 'undefined' || !document.hidden);
  const wrap = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const mq = window.matchMedia(RM);
    const on = () => setReduced(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  useEffect(() => {
    const on = () => setTabShown(!document.hidden);
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);

  useEffect(() => {
    const section = wrap.current?.parentElement;
    if (!section) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0 });
    io.observe(section);
    return () => io.disconnect();
  }, []);

  const shouldPlay = !reduced && !failed && !userPaused && visible && tabShown;

  useEffect(() => {
    const v = video.current;
    if (!v) return;
    if (!shouldPlay) { v.pause(); return; }
    let cancelled = false;
    v.muted = true;
    v.play().catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [shouldPlay, reduced]);

  useEffect(() => {
    if (reduced || failed) setReady(false);
  }, [reduced, failed]);

  const toggle = () => {
    if (failed) { setFailed(false); setUserPaused(false); return; }
    setUserPaused(p => !p);
  };
  const playing = !userPaused && !failed;

  return (
    <>
      <div ref={wrap} aria-hidden="true" data-testid="hero-background-media" className="absolute inset-0 -z-10 pointer-events-none overflow-hidden">
        <img src={posterImg} alt="" data-testid="hero-background-poster" className="absolute inset-0 w-full h-full object-cover" />
        {!reduced && !failed && (
          <video
            ref={video}
            data-testid="hero-background-video"
            data-ready={ready}
            src={heroVideo}
            poster={posterImg}
            muted
            loop
            playsInline
            preload="metadata"
            tabIndex={-1}
            onPlaying={() => setReady(true)}
            onError={() => setFailed(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${ready ? 'opacity-100' : 'opacity-0'}`}
          />
        )}
        <div className="absolute inset-0 bg-white/80 lg:bg-white/35" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/75 via-white/30 to-transparent" />
      </div>
      {!reduced && (
        <button
          type="button"
          onClick={toggle}
          aria-pressed={!playing}
          data-testid="button-hero-background-toggle"
          className="absolute z-30 right-6 bottom-6 inline-flex items-center gap-2.5 px-3.5 py-2.5 bg-white/85 border border-border font-mono text-[10px] tracking-[0.2em] uppercase text-foreground hover:bg-white transition-colors"
        >
          {failed || !playing ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
          {failed ? 'Retry video' : playing ? 'Pause video' : 'Play video'}
        </button>
      )}
    </>
  );
}
